import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';

// Types
import { CollectionPointProps } from 'types/CollectionPoint.types';
import { AppDispatch } from 'types/RootState.types';
import { ClickAndCollectProps } from './ClickAndCollect.types';

// Config
import clickAndCollectState from './ClickAndCollect.state';
import { VIEW_TYPE_LIST } from '@constants/clickCollectConstants';
import routes from '@constants/routeConstants';
import scrollConstants from '@constants/scrollConstants';
import {
  closeMoreDetailsModal,
  selectCollectionPoint,
  setActiveModalMapMarker,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import { scrollToElement } from '@utils/index';

// Components
import ClickAndCollectSearch from '@components/click-and-collect-search';
import ClickAndCollectSelectedPoint from '@components/click-and-collect-selected-point';
import ClickAndCollectStoreMapView from '@components/click-and-collect-store-map-view';
import Container from '@components/container';
import Heading from '@components/heading';
import SavedCollectionPointsModal from '@components/saved-collection-points-modal';
import ScrollAnchor from '@components/scroll-anchor';
import ClickAndCollectResults from '@views/click-and-collect/click-and-collect-results';

const ClickAndCollect = ({
  disabled = false,
  prevCollectionPointSearchActive,
  prevSelectedCollectionPoint,
}: ClickAndCollectProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    collectionPointSearchResults = [],
    collectionPointSearchTerm,
    collectionPointSearchActive = false,
    collectionPointSearchComplete = false,
    findCollectionPointError,
    storeDetailsModalOpen = false,
    modalShowSelectShopButton = false,
    selectedCollectionPoint,
    totalCollectionPoints = 0,
    showSavedCollectionPointsModal = false,
    savedCollectionPoints = [],
    activeModalMapMarker,
    getCollectionPointDetailsCallActive = false,
    getCollectionPointDatesApiCallActive = false,
    getSavedCollectionPointsApiCallLoading = false,
    activeViewType = VIEW_TYPE_LIST,
    collectionPointsOwners = [],
    isFreeCollection = true,
    activeMapMarker,
    countdownVisible = false,
    cutOffTimeForAllStores,
    earliestCollectionDateTime,
    shopsToShow,
    selectingCollectionPointId,
    storeToPromote,
    hideMapInStoreDetailsModal,
  } = useSelector(clickAndCollectState);

  const [autoFocusMoreDetailsButton, setAutoFocusMoreDetailsButton] = useState<boolean>(false);

  const prevValues = useRef<{
    collectionPointSearchActive: boolean;
    selectedCollectionPoint?: CollectionPointProps;
  }>({
    collectionPointSearchActive: prevCollectionPointSearchActive || collectionPointSearchActive,
    selectedCollectionPoint: prevSelectedCollectionPoint || selectedCollectionPoint,
  });

  useEffect(() => {
    const {
      collectionPointSearchActive: currCollectionPointSearchActive,
      selectedCollectionPoint: currValueSelectedCollectionPoint,
    } = prevValues.current;

    const hasSearchCompleted = currCollectionPointSearchActive && !collectionPointSearchActive;
    if (hasSearchCompleted) {
      scrollToElement({ id: scrollConstants.CLICK_COLLECT_SEARCH_RESULTS_TOP });
    }

    if (selectedCollectionPoint) {
      const selectedCollectionPointChanged = currValueSelectedCollectionPoint?.id !== selectedCollectionPoint.id;

      if (selectedCollectionPointChanged) {
        scrollToElement({ id: scrollConstants.DELIVERY_CHOICE });
      }
    }

    prevValues.current = { collectionPointSearchActive, selectedCollectionPoint };
  }, [collectionPointSearchActive, selectedCollectionPoint]);

  const handleModalSelectShopClick = (collectionPoint?: CollectionPointProps) => {
    dispatch(selectCollectionPoint({ collectionPoint }));
    setAutoFocusMoreDetailsButton(true);
  };

  const onCloseHandler = () => {
    dispatch(closeMoreDetailsModal());
  };

  const setActiveModalMapMarkerHandler = (marker: CollectionPointProps) => {
    dispatch(setActiveModalMapMarker(marker));
  };

  const renderSelectedCollectionPointContent = () => (
    <>
      <Container renderIf={!!selectedCollectionPoint}>
        <ClickAndCollectSelectedPoint
          autoFocusMoreDetailsButton={autoFocusMoreDetailsButton}
          collectionPoint={selectedCollectionPoint}
          disabled={disabled}
        />
      </Container>

      <Container renderIf={storeDetailsModalOpen}>
        <ClickAndCollectStoreMapView
          activeMarker={activeModalMapMarker}
          getCollectionPointDatesApiCallActive={getCollectionPointDatesApiCallActive}
          handleMarkerClick={setActiveModalMapMarkerHandler}
          handleSelectShopClick={() => handleModalSelectShopClick(activeModalMapMarker)}
          hideMapInStoreDetailsModal={hideMapInStoreDetailsModal}
          markers={collectionPointSearchResults}
          onClose={onCloseHandler}
          searchTerm={collectionPointSearchTerm}
          showSelectShopButton={modalShowSelectShopButton}
        />
      </Container>

      <Container renderIf={showSavedCollectionPointsModal}>
        <SavedCollectionPointsModal
          baseRoutePath={routes.CLICK_AND_COLLECT}
          collectionPoints={savedCollectionPoints}
          getCollectionPointDatesApiCallActive={getCollectionPointDatesApiCallActive}
          isOpen
          selectedCollectionPoint={selectedCollectionPoint}
        />
      </Container>
    </>
  );

  const isLoading =
    getCollectionPointDetailsCallActive ||
    getCollectionPointDatesApiCallActive ||
    getSavedCollectionPointsApiCallLoading;

  return (
    <Container background="white" padding="3" testId="click-and-collect-container" width="full">
      <ScrollAnchor scrollId={scrollConstants.CLICK_COLLECT_TOP} />

      {isLoading && (
        <Container flex={{ justifyContent: 'center' }}>
          <LoadingSpinner />
        </Container>
      )}

      {!selectedCollectionPoint && !getCollectionPointDetailsCallActive && !getSavedCollectionPointsApiCallLoading && (
        <>
          {!getCollectionPointDatesApiCallActive && (
            <Container width="full">
              <Heading label="Find a collection point" marginBottom="2" tag="h3" type="s" />

              <ClickAndCollectSearch
                clickAndCollectCollectionPointFieldLabel="Enter a postcode, town or shop name"
                collectionPointSearchTerm={collectionPointSearchTerm}
                disabled={disabled}
                findCollectionPointError={findCollectionPointError}
              />
            </Container>
          )}

          {collectionPointSearchComplete && totalCollectionPoints > 0 && (
            <>
              <ScrollAnchor scrollId={scrollConstants.CLICK_COLLECT_SEARCH_RESULTS_TOP} />
              <ClickAndCollectResults
                activeMapMarker={activeMapMarker}
                activeViewType={activeViewType}
                collectionPointSearchComplete={collectionPointSearchComplete}
                collectionPointSearchResults={collectionPointSearchResults}
                collectionPointsOwners={collectionPointsOwners}
                countdownVisible={countdownVisible}
                cutOffTimeForAllStores={cutOffTimeForAllStores}
                disabled={disabled}
                earliestCollectionDateTime={earliestCollectionDateTime}
                getCollectionPointDatesApiCallActive={getCollectionPointDatesApiCallActive}
                isFreeCollection={isFreeCollection}
                searchTerm={collectionPointSearchTerm}
                selectedCollectionPoint={selectedCollectionPoint}
                selectingCollectionPointId={selectingCollectionPointId}
                shopsToShow={shopsToShow}
                storeToPromote={storeToPromote}
                totalCollectionPoints={totalCollectionPoints}
              />
            </>
          )}
        </>
      )}

      {renderSelectedCollectionPointContent()}
    </Container>
  );
};

export default ClickAndCollect;
